import { dbGet, dbPut, type StoreName } from "./db.js";

/**
 * Device-side catalog cache for channels + VOD responses (technical-spec
 * §6.2). Same layering rule as the EPG cache: the cached copy is a *seed*
 * that paints instantly on a cold load while the network refresh runs in the
 * background; every successful fetch write-throughs, so the device copy is
 * never older than the last successful upload.
 *
 * Unlike EPG (whose `fetchedAt` stamps the feed), the channels/VOD responses
 * are raw arrays with no server stamp — the device write time (`savedAt`) is
 * the version stamp, and the newest successful fetch always wins.
 */

export interface CatalogCacheEntry<T> {
  /** Device write time (ISO) — the version stamp for this entry. */
  savedAt: string;
  data: T;
}

export function catalogCacheKey(kind: "channels" | "vod", sourceId: string): string {
  return `${kind}|${sourceId}`;
}

/** Read a cached catalog entry. Resolves null on any failure — callers treat
 * null as "no cache" and just wait on the network. */
export async function readCatalogCache<T>(store: StoreName, key: string): Promise<CatalogCacheEntry<T> | null> {
  try {
    const entry = await dbGet<CatalogCacheEntry<T>>(store, key);
    if (!entry || typeof entry !== "object" || !("data" in entry)) return null;
    return entry;
  } catch {
    return null;
  }
}

/**
 * Write-through with a sparse guard: the newest successful fetch replaces the
 * cache, but a smaller/empty response never clobbers a fuller cached one
 * (provider throttles can return partial catalogs mid-refresh).
 */
export async function writeCatalogCache<T>(
  store: StoreName,
  key: string,
  data: T,
  sizeOf: (data: T) => number,
): Promise<void> {
  try {
    const existing = await readCatalogCache<T>(store, key);
    if (existing && sizeOf(existing.data) > sizeOf(data)) return;
    const entry: CatalogCacheEntry<T> = { savedAt: new Date().toISOString(), data };
    await dbPut(store, entry, key);
  } catch {
    // Non-fatal: cache is an optimization, never a dependency.
  }
}
